import React, {useState, useEffect, useRef} from "react";
import {useNavigate, NavigateFunction, Link, useLocation, useParams} from "react-router-dom";
import {fetchStations, getBatteryValue} from "../../services/api.service";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    ColumnDef,
    SortingState,
} from '@tanstack/react-table';

type Station = {
    id: string;
    name: string;
    countryCode: string;
    battery: number
    active: boolean;
    offline24h: boolean;
    offlineWeek: boolean;
    delayed: boolean;
    lastFlag?: string | null;
    lastMeasurement?: string | null;
    [key: string]: any; // to allow extra fields without strict typing
};

const StationList: React.FC = () => {
    const navigate: NavigateFunction = useNavigate();
    const [stations, setStations] = useState<Station[]>([]);
    const [loading, setLoading] = useState(false);
    const {code} = useParams(); // for /country/:code
    const {status} = useParams(); // for /status/:status
    const location = useLocation();
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([]);
    const [batteryMeans, setBatteryMeans] = useState<Record<string, number>>({}); // id -> mean (ro

    // In-memory session cache to avoid re-fetching means for the same id
    const meansCacheRef = useRef<Map<string, number>>(new Map());
    // Track in-flight requests to avoid duplicate fetches
    const inflightRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        const loadStations = async () => {
            setLoading(true);

            const isFlagged = location.pathname.includes('flagged');
            const country = code;
            const station_status = status;

            try {
                const data = await fetchStations(country, isFlagged, station_status);

                setStations(data);
            } catch (error) {
                console.error('Failed to fetch stations', error);
            } finally {
                setLoading(false);
            }
        };

        loadStations();
    }, [location.pathname, code, status]);

    const getBatteryProgressBarClass = (value: number) => {
        if (value < 20) return 'bg-danger';  // red
        if (value < 60) return 'bg-warning'; // yellow
        return 'bg-success'; // green
    };

    const stationColumns: ColumnDef<Station>[] = [
        {
            header: () => 'Station Code',
            accessorKey: 'id',
            cell: info => (
                <Link to={`/stations/${info.getValue()}`} className="text-primary">
                    {info.getValue() as string}
                </Link>
            ),
            filterFn: 'includesString',
        },
        {
            header: () => 'Station Name',
            accessorKey: 'name',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            header: () => 'Last Measurement',
            accessorKey: 'lastMeasurement',
            cell: info => info.getValue(),
        },
        {
            header: () => 'Last Flag',
            accessorKey: 'lastFlag',
            cell: info => info.getValue(),
        },
        {
            header: () => 'Battery Level',
            accessorKey: 'battery',
            cell: info => {
                const row = info.row.original;
                const mean = batteryMeans[row.id]; // number | undefined
                const isReady = typeof mean === 'number';

                if (!isReady) {
                    return (
                        <div className="mb-0 text-center">
                            <div className="d-flex justify-content-center">
                                <div className="spinner-border spinner-border-sm text-muted" role="status"
                                     aria-label="loading-48h-mean">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                            <small className="text-muted d-block mt-1">Loading 48-hour average...</small>
                        </div>
                    );
                }

                const display = Math.round(mean);
                return (
                    <div className="mb-0">
                        <div className="progress-w-percent">
                            <span className="progress-value">{display}%</span>
                            <div className="progress progress-sm">
                                <div
                                    className={`progress-bar ${getBatteryProgressBarClass(display)}`}
                                    role="progressbar"
                                    style={{width: `${display}%`}}
                                    aria-valuenow={display}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                />
                            </div>
                        </div>
                        <small className="text-muted d-block mt-1">48-hour average</small>
                    </div>
                );


            }
        },
        {
            header: () => 'Status',
            accessorKey: 'active',
            cell: info => {
                const isActive = info.getValue() as boolean;
                return (
                    <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
                {isActive ? 'Active' : 'Not Active'}
            </span>

                );
            }
        },
        {
            header: () => 'Delayed',
            accessorKey: 'delayed',
            cell: info => {
                const isDelayed = info.getValue() as boolean;
                return (
                    <span className={`badge ${isDelayed ? 'badge-danger-lighten' : 'badge-success-lighten'}`}>
                {isDelayed ? 'Delayed' : 'On Time'}
            </span>
                );
            }
        },

        {
            header: () => 'Offline 24h',
            accessorKey: 'offline24h',
            cell: info => {
                const isOffline = info.getValue() as boolean;
                return (
                    <span className={`badge ${isOffline ? 'badge-danger-lighten' : 'badge-success-lighten'}`}>
                        {isOffline ? 'Offline' : 'Online'}
                    </span>
                );
            }
        },
        {
            header: () => 'Offline Week',
            accessorKey: 'offlineWeek',
            cell: info => {
                const isOffline = info.getValue() as boolean;
                return (
                    <span className={`badge ${isOffline ? 'badge-danger-lighten' : 'badge-success-lighten'}`}>
                {isOffline ? 'Offline' : 'Online'}
            </span>
                );

            }
        },

    ];

    const table = useReactTable<Station>({
        data: stations,
        columns: stationColumns,
        state: {
            globalFilter,
            sorting,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: 'includesString',
    });

     // Helper: limit concurrency of async tasks
  async function runWithConcurrency<T>(items: string[], limit: number, task: (id: string) => Promise<T>) {
    const results: Array<Promise<void>> = [];
    const iterator = items[Symbol.iterator]();
    let active = 0;

    return new Promise<void>((resolve) => {
      const next = () => {
        const n = iterator.next();
        if (n.done) {
          if (active === 0) resolve();
          return;
        }
        active += 1;
        const id = n.value;
        task(id)
          .catch(() => { /* swallow errors per item */ })
          .finally(() => {
            active -= 1;
            next();
          });
      };

      const kick = Math.min(limit, items.length);
      for (let i = 0; i < kick; i++) next();
      if (items.length === 0) resolve();
    });
  }

  // Compute visible station IDs (current page rows)
  const visibleIds = table.getRowModel().rows.map(r => (r.original.id));

  // Prefetch next page ids (optional)
  const pagination = table.getState().pagination;
  const pageIndex = pagination?.pageIndex ?? 0;
  const pageSize = pagination?.pageSize ?? 10;
  const startNext = (pageIndex + 1) * pageSize;
  const endNext = startNext + pageSize;
  const nextPageIds = stations.slice(startNext, endNext).map(s => s.id);

  useEffect(() => {
    let cancelled = false;

    const fetchMeans = async (ids: string[]) => {
      // Filter to ids not already cached or in-flight
      const targets = ids.filter(id => !meansCacheRef.current.has(id) && !inflightRef.current.has(id));
      if (targets.length === 0) return;

      // Mark in-flight
      targets.forEach(id => inflightRef.current.add(id));

      // Limit concurrency (e.g., 4 at a time)
      await runWithConcurrency(targets, 4, async (id) => {
        try {
          const v = await getBatteryValue(id, 'mean'); // returns number|null
          if (cancelled) return;
          if (v != null && Number.isFinite(v)) {
            const rounded = Math.round(v);
            meansCacheRef.current.set(id, rounded);
            // Update UI (progressive)
            setBatteryMeans(prev => (prev[id] === rounded ? prev : { ...prev, [id]: rounded }));
          } else {
            // Cache null as a miss so we don't hammer the API repeatedly
            meansCacheRef.current.set(id, NaN);
          }
        } catch (e) {
          // Mark failure to avoid re-fetch loop
          meansCacheRef.current.set(id, NaN);
        } finally {
          inflightRef.current.delete(id);
        }
      });
    };

    // Fetch for current page first (visible)
    fetchMeans(visibleIds)
      .then(() => fetchMeans(nextPageIds)) // then prefetch next page (optional)
      .catch(() => { /* ignore */ });

    return () => { cancelled = true; };
  }, [visibleIds.join(','), nextPageIds.join(','), pageIndex, pageSize]); // deps on pagination/visible set


    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box">
                                <h4 className="page-title">Stations</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h4 className="header-title">Stations</h4>
                                    <input
                                        className="form-control form-control-sm"
                                        style={{width: '250px'}}
                                        placeholder="Search..."
                                        value={globalFilter ?? ''}
                                        onChange={e => setGlobalFilter(e.target.value)}
                                    />
                                </div>
                                <div className="card-body pt-0">
                                    {loading ? (
                                        <p>Loading...</p>
                                    ) : (
                                        <>
                                            <table
                                                className="table dt-responsive nowrap w-100"
                                            >
                                                <thead>
                                                {table.getHeaderGroups().map(headerGroup => (
                                                    <tr key={headerGroup.id}>
                                                        {headerGroup.headers.map(header => (
                                                            <th
                                                                key={header.id}
                                                                onClick={header.column.getToggleSortingHandler()}
                                                                style={{cursor: 'pointer'}}
                                                            >
                                                                {flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                                {header.column.getIsSorted() === 'asc' && ' 🔼'}
                                                                {header.column.getIsSorted() === 'desc' && ' 🔽'}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                ))}
                                                </thead>
                                                <tbody>
                                                {table.getRowModel().rows.map(row => (
                                                    <tr key={row.id}>
                                                        {row.getVisibleCells().map(cell => (
                                                            <td key={cell.id}>
                                                                {flexRender(
                                                                    cell.column.columnDef.cell,
                                                                    cell.getContext()
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                                {table.getRowModel().rows.length === 0 && (
                                                    <tr>
                                                        <td colSpan={stationColumns.length}
                                                            className="text-center text-muted">
                                                            No results found.
                                                        </td>
                                                    </tr>
                                                )}

                                                </tbody>
                                            </table>
                                            <div
                                                className="card-footer d-flex justify-content-between align-items-center">
                                                <div>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary me-2"
                                                        onClick={() => table.previousPage()}
                                                        disabled={!table.getCanPreviousPage()}
                                                    >
                                                        Previous
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => table.nextPage()}
                                                        disabled={!table.getCanNextPage()}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                                <span>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
                                                    {table.getPageCount()}
          </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );


}
export default StationList;